import { useState } from 'react'
import { motion } from 'framer-motion'
import { RiEdit2Line, RiDeleteBin6Line, RiEyeLine } from 'react-icons/ri'
import { FaRegBuilding } from 'react-icons/fa'
import { Button } from '../../../components/Button'
import { Loading } from '../../../components'
import { ConfirmModal } from '../../../components/Modal'
import type { Processo } from '../../../models'
import { UserAvatar } from '../../../components/UserAvatar'
import type { ProcessPermissions } from '../hooks/usePermissions'
import { useSortableTable } from '../hooks/useSortableTable'
import { SortableHeader } from './SortableHeader'
import { processRichTextForDisplay } from '@/utils'

interface ProcessosTableProps {
  processos: Processo[]
  permissions: ProcessPermissions
  isLoading?: boolean
  onEdit: (processo: Processo) => void
  onDelete: (processo: Processo) => void
  onView?: (processo: Processo) => void
  searchQuery?: string
  selectedSetor?: number | null
}

const PriorityBadge = ({ type, days }: { type: 'baixa' | 'media' | 'alta' | 'urgente', days: number }) => {
  const config = {
    baixa: { color: 'bg-blue-100 text-blue-800', label: 'Baixa', dot: 'bg-blue-500' },
    media: { color: 'bg-yellow-100 text-yellow-800', label: 'Média', dot: 'bg-yellow-500' },
    alta: { color: 'bg-orange-100 text-orange-800', label: 'Alta', dot: 'bg-orange-500' },
    urgente: { color: 'bg-red-100 text-red-800', label: 'Urgente', dot: 'bg-red-500' },
  }

  const { color, label, dot } = config[type]

  return (
    <div className={`inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-xs font-medium ${color}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${dot}`}></div>
      <span>{label}</span>
      <span className="text-xs opacity-75">{days}d</span>
    </div>
  )
}

export const ProcessosTable = ({ 
  processos, 
  permissions, 
  isLoading = false, 
  onEdit, 
  onDelete, 
  onView,
  searchQuery = '',
  selectedSetor = null
}: ProcessosTableProps) => {
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; processo: Processo | null }>({
    isOpen: false,
    processo: null
  })
  const [isDeleting, setIsDeleting] = useState(false)

  // Os dados já vêm filtrados do servidor via TanStack Query
  // searchQuery e selectedSetor são usados apenas para exibir mensagens apropriadas

  // Hook de ordenação
  const { sortedData, sortConfig, requestSort } = useSortableTable(processos)

  const handleDeleteClick = (processo: Processo) => {
    setDeleteModal({ isOpen: true, processo })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.processo) return
    
    try {
      setIsDeleting(true)
      await onDelete(deleteModal.processo)
      setDeleteModal({ isOpen: false, processo: null })
    } catch (error) {
      console.error('Erro ao excluir processo:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, processo: null })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700">
        <div className="p-8 text-center space-y-4">
          <Loading message="Carregando processos..." />
          <p className="text-gray-600 dark:text-gray-400">Aguarde enquanto buscamos os dados</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (sortedData.length === 0 && !isLoading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700">
        <div className="p-12 text-center">
          <div className="inline-flex p-4 rounded-full bg-gray-100 dark:bg-neutral-700 mb-4">
            <RiEyeLine className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {searchQuery || selectedSetor ? 'Nenhum processo encontrado' : 'Nenhum processo cadastrado'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchQuery || selectedSetor
              ? 'Nenhum processo corresponde aos filtros aplicados.'
              : permissions.isUserNormal 
                ? 'Não há processos cadastrados no seu setor.'
                : 'Ainda não há processos cadastrados.'
            }
          </p>
          {!searchQuery && !selectedSetor && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Use o botão &quot;Criar&quot; para adicionar o primeiro processo.</p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-gray-200 dark:border-neutral-700 overflow-hidden flex flex-col h-[calc(100vh-230px)]">
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <table className="w-full divide-y divide-gray-200 dark:divide-neutral-700 table-fixed" style={{ minWidth: '1150px' }}>
            <thead className="bg-gray-50 dark:bg-neutral-700 sticky top-0 z-10">
              <tr>
                <SortableHeader
                  label="Processo"
                  sortKey="nome_processo"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="w-[250px] max-w-[250px]"
                />
                <SortableHeader
                  label="Tipo"
                  sortKey="tipo"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="w-[90px] max-w-[90px]"
                />
                <SortableHeader
                  label="Setor"
                  sortKey="setor.nome_setor"
                  currentSort={sortConfig}
                  onSort={requestSort}
                  className="w-[120px] max-w-[120px]"
                />
                <th className="w-[120px] max-w-[120px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Prazos por Prioridade
                </th>
                <th className="w-[120px] max-w-[120px] px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="w-[120px] max-w-[120px] px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-gray-200 dark:divide-neutral-700">
              {sortedData.map((processo, index) => (
                <motion.tr
                  key={processo.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors"
                >
                  <td className="px-4 py-4 w-[250px] max-w-[250px]">
                    <div className="flex items-center overflow-hidden">
                      <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                        <RiEyeLine className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="ml-4 min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={processo.nome_processo}>
                          {processo.nome_processo}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">ID: {processo.id}</div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 w-[90px] max-w-[90px]">
                    <div className="text-sm text-gray-900 dark:text-gray-100 truncate" title={processo.tipo || 'Não informado'}>
                      {processo.tipo || (
                        <span className="text-gray-400 italic">Não informado</span>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 w-[120px] max-w-[120px]">
                    {processo.setor ? (
                        <div className="flex items-center min-w-0 overflow-hidden">
                          <FaRegBuilding className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={processo.setor.nome_setor}>
                              {processo.setor.nome_setor}
                            </div>
                            {processo.setor.chefe && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center mt-1 overflow-hidden" title={processo.setor.chefe.name}>
                                <UserAvatar user={processo.setor.chefe} size="xs" className="mr-1 flex-shrink-0" />
                                <span className="truncate">{processo.setor.chefe.name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400 truncate">Setor não encontrado</span>
                      )}
                  </td>
                  
                  <td className="px-4 py-4 w-[220px] max-w-[220px]">
                    <div className="flex flex-wrap gap-1 overflow-hidden">
                      <PriorityBadge type="baixa" days={processo.prioridade_baixa} />
                      <PriorityBadge type="media" days={processo.prioridade_media} />
                      <PriorityBadge type="alta" days={processo.prioridade_alta} />
                      <PriorityBadge type="urgente" days={processo.prioridade_urgente} />
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 w-[200px] max-w-[200px]">
                    <div className="text-sm text-gray-900 dark:text-gray-100 line-clamp-3 overflow-hidden rich-text-content" title={processo.descricao}>
                      <div dangerouslySetInnerHTML={{ __html: processRichTextForDisplay(processo.descricao || '') }} />
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-center w-[120px] max-w-[120px]">
                    <div className="flex items-center justify-center space-x-2">
                      {onView && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onView(processo)}
                          className="text-gray-600 hover:text-blue-600"
                          title="Visualizar processo"
                        >
                          <RiEyeLine className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {permissions.canEditProcessos && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(processo)}
                          className="text-gray-600 hover:text-blue-600"
                          title="Editar processo"
                        >
                          <RiEdit2Line className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {permissions.canDeleteProcessos && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(processo)}
                          className="text-gray-600 hover:text-red-600"
                          title="Excluir processo"
                        >
                          <RiDeleteBin6Line className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {/* Mostrar se não há ações disponíveis */}
                      {!permissions.canEditProcessos && !permissions.canDeleteProcessos && !onView && (
                        <span className="text-xs text-gray-400 italic">Somente leitura</span>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        <ConfirmModal
          isOpen={deleteModal.isOpen}
          onClose={handleDeleteCancel}
          type="danger"
          title="Confirmar Exclusão"
          message={`Tem certeza que deseja excluir o processo "${deleteModal.processo?.nome_processo}"?`}
          details="Esta ação não pode ser desfeita."
          confirmText="Excluir"
          cancelText="Cancelar"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          loading={isDeleting}
        />
      </div>
  )
}
